/**
 * Post-redeem instance success panel with a GATED "Open my new instance"
 * CTA (OCP-457).
 *
 * The redeem success screen used to render the primary CTA + magic-login
 * link as an active button immediately, before the tenant was reachable.
 * Provisioning takes minutes, so a click landed on a not-yet-served
 * `https://<sub>.orbo.to` and the customer hit a TLS cert error
 * (NET::ERR_CERT_AUTHORITY_INVALID) or a 502 - a scary first impression
 * for a brand-new paying customer.
 *
 * This panel reuses the OCP-204/205 lifecycle polling mechanism
 * (`ProvisioningProgress` -> `GET /v1/portal/instances/:id/lifecycle`) to
 * drive three phases:
 *
 *   - provisioning: live step checklist + a DISABLED CTA with a spinner
 *                   and a "still provisioning" hint. The magic-login link
 *                   is gated the same way.
 *   - ready:        tenant reached `active` (poll_ready already gates
 *                   `active` on the URL serving 200, so active implies
 *                   reachable + cert issued) -> CTA enabled, hrefs the
 *                   magic-login URL (`https://<sub>.orbo.to/...`).
 *   - failed:       a PERMANENT provision failure (OCP-488: all pg-boss
 *                   retries exhausted). Presented CALMLY, never as a red
 *                   error: the customer is told their workspace will be
 *                   emailed to them, the checklist is hidden, and a human
 *                   operator has already been paged server-side. A single
 *                   transient failed step is NOT this - it keeps polling
 *                   and self-heals (the mixup.orbo.to incident recovered
 *                   ~2.5 min after a failed cloudflare_cname step).
 *
 * Terminal detection: `ProvisioningProgress` calls `onTerminal` once when
 * polling reaches a terminal state, passing `{ status, failed }`. `failed`
 * is the endpoint's `provisioning_failed` flag (permanent only). The gate
 * is "active => ready, otherwise => failed (calm)".
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ProvisioningProgress } from './ProvisioningProgress';

type Phase = 'provisioning' | 'ready' | 'failed';

interface Props {
  tenantId: string;
  subdomain: string;
  /**
   * Fresh magic-login URL to SSO the customer into the new tenant.
   * Present on the post-redeem success flow. Absent on the OCP-461
   * re-entry guard (returning to /redeem for an already-consumed code):
   * we have no fresh setup token then, so the ready-state CTA links to
   * My Instances instead of a magic-login.
   */
  magicLoginUrl?: string;
  /**
   * Poll interval forwarded to ProvisioningProgress; defaults to the
   * component's own 2000ms. Tests override it for determinism.
   */
  pollIntervalMs?: number;
}

export function InstanceProvisioningSuccess({
  tenantId,
  subdomain,
  magicLoginUrl,
  pollIntervalMs,
}: Props) {
  const { t } = useTranslation('instance-detail');
  const [phase, setPhase] = useState<Phase>('provisioning');
  const host = `${subdomain}.orbo.to`;

  const handleTerminal = useCallback((result: { status: string; failed: boolean }) => {
    // active => reachable (poll_ready gated active on HTTPS 200 + valid cert).
    // `failed` is a PERMANENT provision failure only (OCP-488) - a transient
    // failed step never reaches a terminal state, it keeps polling.
    setPhase(result.status === 'active' ? 'ready' : 'failed');
  }, []);

  // OCP-488 - the "failed" phase is presented CALMLY, never as a red error.
  // The provision is escalated to a human operator server-side; the customer
  // is told their workspace will be emailed to them, not dead-ended on an
  // error screen. Neutral slate styling, not the alarming red it used to be.
  const wrapperCls =
    phase === 'failed'
      ? 'rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-6'
      : 'rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 p-6';

  const headingCls =
    phase === 'failed'
      ? 'text-lg font-semibold text-slate-900 dark:text-slate-100'
      : 'text-lg font-semibold text-emerald-900 dark:text-emerald-200';

  const bodyCls =
    phase === 'failed'
      ? 'mt-2 text-sm text-slate-700 dark:text-slate-300'
      : 'mt-2 text-sm text-emerald-800 dark:text-emerald-300';

  return (
    <div className={wrapperCls} data-testid="instance-success-panel" data-phase={phase}>
      <h2 className={headingCls}>
        {phase === 'ready'
          ? t('success.titleReady')
          : phase === 'failed'
            ? t('success.titleFailed')
            : t('success.titleProvisioning')}
      </h2>

      <p className={bodyCls}>
        {phase === 'ready' ? (
          magicLoginUrl ? (
            <>
              <span className="font-mono font-medium">{host}</span>
              {t('success.readyMagicAfter')}
            </>
          ) : (
            <>
              <span className="font-mono font-medium">{host}</span>
              {t('success.readyPlainAfter')}
            </>
          )
        ) : phase === 'failed' ? (
          <>
            {t('success.failedBefore')}
            <span className="font-mono font-medium">{host}</span>
            {t('success.failedAfter')}
          </>
        ) : (
          <>
            {t('success.provisioningBefore')}
            <span className="font-mono font-medium">{host}</span>
            {t('success.provisioningAfter')}
          </>
        )}
      </p>

      {/* Live step checklist + terminal detection - reuses the OCP-205
          lifecycle polling mechanism / endpoint. Hidden once we've reached a
          permanent-failure state (OCP-488): the customer sees the calm
          "we'll email you" message above, not a stalled checklist. */}
      {phase !== 'failed' && (
        <div className="mt-4">
          <ProvisioningProgress
            instanceId={tenantId}
            onTerminal={handleTerminal}
            pollIntervalMs={pollIntervalMs}
          />
        </div>
      )}

      <div className="mt-4">
        {phase === 'ready' ? (
          magicLoginUrl ? (
            <a
              href={magicLoginUrl}
              data-testid="open-instance-cta"
              className="inline-flex items-center gap-2 rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            >
              {t('success.openNew')}
              <span aria-hidden>&rarr;</span>
            </a>
          ) : (
            <Link
              to="/instances"
              data-testid="open-instance-cta"
              className="inline-flex items-center gap-2 rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            >
              {t('success.openAll')}
              <span aria-hidden>&rarr;</span>
            </Link>
          )
        ) : phase === 'failed' ? (
          <div
            data-testid="provisioning-failed"
            className="rounded border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-950/40 px-3 py-2 text-sm text-slate-700 dark:text-slate-300"
          >
            {t('success.failedBox')}
            <a
              href="mailto:support@orboto.io"
              className="font-medium underline hover:no-underline"
            >
              {t('success.helpLink')}
            </a>
            .
          </div>
        ) : (
          <div>
            <button
              type="button"
              disabled
              data-testid="open-instance-cta"
              className="inline-flex cursor-not-allowed items-center gap-2 rounded bg-emerald-600/60 px-3 py-2 text-sm font-medium text-white/90"
              aria-label={t('success.openNewAria')}
            >
              <span
                aria-hidden
                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/70 border-t-transparent"
              />
              {t('success.openNew')}
            </button>
            <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400">
              {t('success.stillProvisioning')}
            </p>
          </div>
        )}
      </div>

      <div className="mt-3">
        <Link
          to="/instances"
          className={
            phase === 'failed'
              ? 'text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
              : 'text-sm text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100'
          }
        >
          {t('success.viewAll')} &rarr;
        </Link>
      </div>
    </div>
  );
}
